"use client";

import { useState } from "react";
import Link from "next/link";
import { ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/Button";
import { BattleNetIcon } from "@/components/icons/BattleNetIcon";
import { bnetLoginUrl } from "@/lib/auth";
import { cn } from "@/lib/utils";

const REGIONS: { value: "us" | "eu" | "kr" | "tw"; label: string }[] = [
  { value: "eu", label: "Europe" },
  { value: "us", label: "Americas" },
  { value: "kr", label: "Korea" },
  { value: "tw", label: "Taiwan" },
];

export default function LoginPage() {
  const [region, setRegion] = useState<"us" | "eu" | "kr" | "tw">("eu");

  function signIn() {
    // Full-page redirect — required for the OAuth flow. The backend's
    // /api/auth/bnet/login sets a CSRF cookie and 302's to Battle.net.
    window.location.href = bnetLoginUrl(region);
  }

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm">
        <div className="text-center">
          <Logo size="md" className="justify-center" />
          <h1 className="mt-8 font-display text-2xl font-semibold tracking-tight text-fg">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-fg-muted">
            Save your characters and rolls across devices.
          </p>
        </div>

        {/* Region picker — Battle.net OAuth is region-scoped. */}
        <div className="mt-8">
          <p className="eyebrow mb-2">Region</p>
          <div className="grid grid-cols-4 gap-1 rounded-sm border border-border bg-surface p-0.5">
            {REGIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setRegion(r.value)}
                className={cn(
                  "h-8 rounded-xs text-xs font-mono uppercase tracking-wider transition-colors",
                  region === r.value
                    ? "bg-surface-3 text-fg"
                    : "text-fg-subtle hover:text-fg hover:bg-surface-2",
                )}
              >
                {r.value.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <Button
            variant="battlenet"
            size="lg"
            className="w-full"
            onClick={signIn}
          >
            <BattleNetIcon size={18} />
            Continue with Battle.net
          </Button>
          <p className="text-center text-xs text-fg-subtle">
            Or{" "}
            <Link
              href="/dashboard"
              className="text-fg-muted hover:text-fg transition-colors underline underline-offset-2"
            >
              continue without signing in
            </Link>
          </p>
        </div>

        <div className="mt-10 rounded-md border border-border bg-surface/40 p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-4 w-4 text-success shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-fg">Read-only access</p>
              <p className="mt-1 text-xs text-fg-subtle leading-relaxed">
                We use the <code className="font-mono">wow.profile</code> scope
                to pull your character roster + gear. We can&apos;t change
                anything in-game and we never see your password.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-fg-subtle font-mono uppercase tracking-wider">
          Free for personal use
        </p>
      </div>
    </div>
  );
}
